import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AsoebiTab } from '../constants/asoebiTabs';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  Legend,
  PieChart,
  Pie,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useMoney } from '../hooks/useMoney';
import { downloadText } from '../utils/download';
import { formatDate } from '../utils/formatDate';
import { sumDecimal, subtractDecimal } from '../utils/decimal';
import { loadGuests, refreshGuestsFromApi, getGuestStats } from '../services/GuestService';
import { refreshBudgetFromApi } from '../utils/budgetStorage';
import { refreshEventsFromApi, StoredEvent } from '../services/EventsService';
import { BudgetCategory } from '../types/ui/Budget';
import { GuestStats } from '../types/ui/Guests';
import { computeHostingCostBreakdown, computeGuestCostCoverage, type GuestCostCoverage } from '../utils/asoebiAccounting';
import ConfirmDialog from './ui/ConfirmDialog';
import { useNotifications } from './NotificationProvider';
import '../styles/components/AsoebiManager.css';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import FabricCatalog from './FabricCatalog';
import GuestAsoebiTracker from './GuestAsoebiTracker';
import AsoebiOrdersList from './AsoebiOrdersList';
import { isAsoebiOrderDetailUrl } from '../utils/asoebiOrdersRoute';
import BundlesView from './BundlesView';
import { loadBundles, refreshBundlesFromApi } from '../services/BundleService';
import { UnifiedBundle } from '../types/Product';
import { attributeFabricUnits, totalFabricCost, computeBundleSellability } from '../utils/asoebiAttribution';
import { fabricRefName, isBundleRef, refersToBundle } from '../utils/fabricRef';
import { loadBundleAllocations, computeBundleActualCost } from '../services/ItemAllocationService';
import { formatCompactMoney } from '../utils/currency';
import PaymentModal from './PaymentModal';
import { apiGet, planningPath } from '../services/PlanningApiClient';
import ActivityLog from './ActivityLog';
import { setAsoebiOrdersBar } from '../hooks/useAsoebiOrdersBar';
import { Fabric, FabricAssignment, GuestAsoebiProfile, AsoebiStats, AsoebiFilter } from '../types/ui/Asoebi';
import {
  loadFabrics,
  loadAssignments,
  deriveGuestAsoebiProfiles,
  getAsoebiStats,
  filterFabrics,
  updateFabric,
  archiveFabric as deleteFabric,
  recordPayment,
  refreshFabricsFromApi,
  refreshAssignmentsFromApi,
} from '../services/AsoebiService';

/** Statuses that count as money actually earned (and stock actually consumed). */
const COMPLETED_STATUSES = ['paid', 'delivered'];

interface AsoebiManagerProps {
  triggerAssignFabric?: boolean;
  onAssignFabricHandled?: () => void;
  triggerAddFabricTab?: boolean;
  onAddFabricTabHandled?: () => void;
  activeTab?: AsoebiTab;
  onTabChange?: (tab: AsoebiTab) => void;
  triggerExport?: boolean;
  onExportHandled?: () => void;
  triggerReport?: boolean;
  onReportHandled?: () => void;
  fabricId?: string;
  /** Status filter for the tracker tab — controlled from Header breadcrumb */
  trackerFilter?: string;
  /** Deliveries tab filters — controlled from Header breadcrumb */
  deliveryStatusFilter?: string;
  onDeliveryStatusFilterChange?: (v: string) => void;
  deliveryAssigneeFilter?: string;
  onDeliveryAssigneeFilterChange?: (v: string) => void;
  deliveryZoneFilter?: string;
  onDeliveryZoneFilterChange?: (v: string) => void;
  onDeliveryZonesReady?: (zones: { key: string; label: string }[]) => void;
  /** Bundle id parsed from the /bundle/:id URL (App.tsx) — forwarded to
   * BundlesView so it can auto-open that bundle's edit form. */
  openBundleId?: string;
  /** True when landing directly on /bundle/new — forwarded to BundlesView
   * so it auto-opens the create form. */
  openNewBundleOnMount?: boolean;
}

const AsoebiManager: React.FC<AsoebiManagerProps> = ({ triggerAssignFabric, onAssignFabricHandled, triggerAddFabricTab, onAddFabricTabHandled, activeTab: externalTab, onTabChange, triggerExport, onExportHandled, triggerReport, onReportHandled, fabricId, trackerFilter = 'all', deliveryStatusFilter, onDeliveryStatusFilterChange, deliveryAssigneeFilter, onDeliveryAssigneeFilterChange, deliveryZoneFilter, onDeliveryZoneFilterChange, onDeliveryZonesReady, openBundleId, openNewBundleOnMount }) => {
  const { user } = useAuth();
  const { format: formatMoney, currency: projectCurrency } = useMoney();
  const [internalTab, setInternalTab] = useState<AsoebiTab>('overview');
  const activeTab = externalTab ?? internalTab;
  const setActiveTab = (tab: AsoebiTab) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };
  const [triggerAssignForm, setTriggerAssignForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (triggerAssignFabric) {
      // "Assign Fabric" creates an ORDER (a guest's commitment plus the money
      // against it), which is GuestAsoebiTracker's form on the Orders tab —
      // not an assignment. It used to land on the Assignments tab because
      // that tab rendered the very same order form; it now lists real
      // asoebi_assignments rows, whose 3-field form has no guest, quantity or
      // price. Navigating explicitly rather than only flipping the tab: the
      // Orders tab mounts that form only on a detail URL.
      setActiveTab('orders');
      setTriggerAssignForm(true);
      navigate('/asoebi/orders/new');
      onAssignFabricHandled?.();
    }
  }, [triggerAssignFabric, onAssignFabricHandled]);

  useEffect(() => {
    if (triggerAddFabricTab) {
      setActiveTab('fabrics');
      onAddFabricTabHandled?.();
    }
  }, [triggerAddFabricTab, onAddFabricTabHandled]);

  useEffect(() => {
    if (triggerExport) {
      handleExportCSV();
      onExportHandled?.();
    }
  }, [triggerExport]);

  const handleExportCSV = () => {
    const rows = assignments.map(a => {
      const guest = loadGuests().find((g: any) => g.id === a.guest_id);
      // fabric_id names a bundle just as often as a fabric — export whichever
      // it actually is rather than a bare id for every bundle assignment.
      const fabricName = fabricRefName(a, fabrics, bundles, String(a.fabric_id));
      return [
        guest?.name || a.guest_id,
        fabricName,
        a.yards,
        a.price_per_yard,
        a.total_price,
        a.status,
        a.notes || '',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    const csv = ['Guest,Fabric,Yards,Price/Yard,Total,Status,Notes', ...rows].join('\n');
    downloadText('asoebi-orders.csv', csv);
  };

  const { showSuccess, showError } = useNotifications();
  const [fabrics, setFabrics] = useState<Fabric[]>([]);
  const [assignments, setAssignments] = useState<FabricAssignment[]>([]);
  // Bundles were only ever read through loadBundles(), which returns an
  // in-memory cache that nothing on this page ever filled — so the overview's
  // bundle count sat at 0 until the tracker tab had been visited. Cost and the
  // fabric chart now depend on bundle contents, so fetch them here too.
  const [bundles, setBundles] = useState<UnifiedBundle[]>(() => loadBundles());
  // Guest Cost Coverage — does asoebi revenue cover what it actually costs
  // to host that guest (fabric + their share of catering + their seat)?
  // See utils/asoebiAccounting.ts for the category-matching and guest-basis
  // reasoning.
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([]);
  const [events, setEvents] = useState<StoredEvent[]>([]);
  const [guestStats, setGuestStats] = useState<GuestStats | null>(null);
  // Derived from assignments rather than stored — see AsoebiService's
  // deriveGuestAsoebiProfiles for why a separate cache used to exist here
  // and never got written to.
  const guestProfiles: GuestAsoebiProfile[] = useMemo(() => deriveGuestAsoebiProfiles(assignments), [assignments]);
  const [stats, setStats] = useState<AsoebiStats | null>(null);
  const [filter, setFilter] = useState<AsoebiFilter>({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<FabricAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteFabricTarget, setDeleteFabricTarget] = useState<string | null>(null);
  // A bundle's own record page (opened by clicking a bundle, or landing
  // directly on /bundle/:id) shows its own activity feed with fabric/package
  // assignments already merged in (see BundlesView.tsx's extraEvents) — skip
  // the page-level list below so it isn't duplicated. Derived straight from
  // these URL-driven props rather than a 'bundle:form-open' event: that event
  // is only dispatched once BundlesView's own effect runs, so a direct URL
  // load raced past this component's render before the event ever fired,
  // silently keeping the duplicate list visible every time.
  const bundleFormOpen = Boolean(openBundleId) || Boolean(openNewBundleOnMount);
  const location = useLocation();
  // The Orders tab is two screens: the list at /asoebi/orders (AsoebiOrdersList)
  // and one order's page at /asoebi/orders/new or /asoebi/orders/<ref>
  // (GuestAsoebiTracker). A direct link or reload to an order's page mounts
  // the tracker straight away; everything else on the tab is the list.
  const showOrderDetail = isAsoebiOrderDetailUrl(location.pathname);
  // Real cash spent sourcing asoebi fabric (procurement_orders tagged to the
  // "Asoebi" budget category, id 20) — separate from Cost/Profit below,
  // which only measure margin on completed guest sales and never touch what
  // was actually paid to source the fabric. Caught 2026-08-25: ~₦9.6M in
  // real Cobuild/silk spend had zero visibility on this page.
  const [totalSpent, setTotalSpent] = useState<number | null>(null);
  // Real procurement cash across EVERY budget category (Asoebi, Decor,
  // Favours, Bride Specific, …) — feeds the Overview's spend-by-category
  // donut. totalSpent above stays scoped to Asoebi (category 20) for the
  // existing "Total Spent" card; this is the additive, all-category view.
  const [categorySpend, setCategorySpend] = useState<{ category_id: number; category_name: string; total_spent_ngn: number }[]>([]);
  // Per-bundle Price / Target Cost / real landed Cost / Margin / Assigned
  // count — feeds the Overview's bundle economics radar. Landed cost comes
  // from the same loadBundleAllocations + computeBundleActualCost pipeline
  // BundlesView's own edit-form sidebar uses (ItemAllocationService.ts), so
  // this can't drift from that number; it's fetched per bundle here since
  // there are only a handful of active asoebi bundles.
  interface BundleEconomicsRow {
    bundleId: string;
    bundleName: string;
    price: number;
    targetCost: number | null;
    landedCost: number | null;
    margin: number | null;
    assignedCount: number;
  }
  const [bundleEconomics, setBundleEconomics] = useState<BundleEconomicsRow[]>([]);

  useEffect(() => {
    void loadData();
    apiGet<{ success: boolean; data: { total_spent_ngn: number } }>(
      planningPath('/procurement/category-spend/20')
    ).then(res => { if (res.success) setTotalSpent(res.data.total_spent_ngn); }).catch(() => {});
    apiGet<{ success: boolean; data: { category_id: number; category_name: string; total_spent_ngn: number }[] }>(
      planningPath('/procurement/category-spend-breakdown')
    ).then(res => { if (res.success) setCategorySpend(res.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const onArchivedSet = (e: Event) => setFilter(prev => ({ ...prev, archived: (e as CustomEvent).detail as boolean }));
    window.addEventListener('fabric-archived-set', onArchivedSet);
    return () => window.removeEventListener('fabric-archived-set', onArchivedSet);
  }, []);

  // The Archived switch asks for a different server-side list (archived=true),
  // not a client-side filter over the active cache — re-fetch on every flip.
  useEffect(() => {
    refreshFabricsFromApi(filter.archived === true).then(setFabrics).catch(() => {});
  }, [filter.archived]);

  const loadData = async () => {
    setLoading(true);
    const [fabricsData, assignmentsData, bundlesData, categoriesData, eventsData, guestsData] = await Promise.all([
      refreshFabricsFromApi(),
      refreshAssignmentsFromApi(),
      refreshBundlesFromApi(),
      refreshBudgetFromApi(),
      refreshEventsFromApi(),
      refreshGuestsFromApi(),
    ]);
    setFabrics(fabricsData);
    // guestProfiles derives from assignments via useMemo — no separate set.
    setAssignments(assignmentsData);
    setBundles(bundlesData);
    setBudgetCategories(categoriesData);
    setEvents(eventsData);
    setGuestStats(getGuestStats(guestsData));
    setStats(getAsoebiStats());
    setLoading(false);

    // Bundle economics for the radar chart — only the same active asoebi
    // bundles the sellability table already covers. Only ~9 bundles exist,
    // so N parallel per-bundle allocation fetches is fine; a batch endpoint
    // would be over-engineering for this size.
    const asoebiBundlesForEconomics = bundlesData.filter(b => b.is_active && b.types.includes('asoebi'));
    const economics = await Promise.all(asoebiBundlesForEconomics.map(async b => {
      let landedCost: number | null = null;
      try {
        const rows = await loadBundleAllocations(b.id);
        landedCost = computeBundleActualCost(rows, b.targetCurrency ?? projectCurrency)?.actualCostPerUnit ?? null;
      } catch {
        // No allocation data yet for this bundle — landedCost stays null,
        // which keeps it (honestly) out of the radar rather than showing 0.
      }
      const price = b.price || 0;
      const assignedCount = assignmentsData.filter(
        a => !a.archived && a.status !== 'pending_confirmation' && refersToBundle(a, b.id, bundlesData)
      ).length;
      return {
        bundleId: b.id,
        bundleName: b.name,
        price,
        targetCost: b.targetCost ?? null,
        landedCost,
        margin: landedCost !== null ? price - landedCost : null,
        assignedCount,
      };
    }));
    setBundleEconomics(economics);
  };

  const handleFabricUpdate = async (updatedFabric: Fabric) => {
    try {
      await updateFabric(updatedFabric.id, updatedFabric);
    } catch (err) {
      // Don't paint the edit into local state — the server still holds the
      // old row, and the next refresh would silently undo what's on screen.
      showError(err instanceof Error ? err.message : 'Failed to save fabric.');
      return;
    }
    setFabrics(fabrics.map(f => f.id === updatedFabric.id ? updatedFabric : f));
    setStats(getAsoebiStats());
  };

  const handleFabricDelete = (fabricId: string) => {
    setDeleteFabricTarget(fabricId);
  };

  const handlePayment = (assignment: FabricAssignment) => {
    setSelectedAssignment(assignment);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async (assignmentId: string, amount: string, method?: string, reference?: string) => {
    let updatedAssignment: FabricAssignment;
    try {
      updatedAssignment = await recordPayment(assignmentId, { payment_amount: amount, payment_method: method ?? null, payment_reference: reference ?? null });
    } catch (err) {
      // A payment that only ever landed in memory is the worst kind of silent
      // failure here — keep the modal open and say so.
      showError(err instanceof Error ? err.message : 'Failed to record payment.');
      return;
    }
    // guestProfiles re-derives on its own once assignments changes below — no separate profile update needed.
    setAssignments(assignments.map(a => a.id === assignmentId ? updatedAssignment : a));
    setStats(getAsoebiStats());
    setShowPaymentModal(false);
    setSelectedAssignment(null);
  };

  const filteredFabrics = filterFabrics(filter, fabrics);

  const recentAssignments = assignments
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const bundleCount = bundles.filter(b => b.types.includes('asoebi')).length;
  const totalFabricStock = fabrics.reduce((sum, f) => sum + (f.stock || 0), 0);
  const uniqueGuestCount = new Set(assignments.map(a => a.guest_id)).size;

  const revenue = Number(sumDecimal(
    assignments.filter(a => COMPLETED_STATUSES.includes(a.status)).map(a => a.total_price)
  ));
  // Cost per yard is Fabric.price: the API maps the preferred supplier's
  // costNGN onto it (see toFabric in gg-main's asoebi repository), because
  // products carries no cost column of its own — supplier costs live in the
  // suppliers JSONB.
  //
  // The units that cost is charged against cannot come from `a.fabric_id === f.id`:
  // that column holds a BUNDLE id for every bundle-flow assignment (live data is
  // 33 x bundle 4 and 4 x bundle 7), so it never matched a fabric, paidYards was
  // always 0 and Cost sat at ₦0 — which in turn made Profit identical to Revenue.
  // attributeFabricUnits resolves both shapes; the chart below shares it, so the
  // two figures cannot drift apart again.
  const paidAttribution = useMemo(
    () => attributeFabricUnits(
      assignments.filter(a => COMPLETED_STATUSES.includes(a.status)),
      fabrics,
      bundles,
    ),
    [assignments, fabrics, bundles],
  );
  const totalCost = totalFabricCost(fabrics, paidAttribution);
  const profit = revenue - totalCost;
  const fmtN = (n: number) => formatCompactMoney(n, projectCurrency);

  // ── Overview charts ──
  // Excludes pending_confirmation alongside archived — a still-unreviewed
  // portal submission (see the Orders tab / PortalOrdersTriage) isn't a real
  // assignment yet, it's just a guest's self-service request nobody has
  // acted on. Counting it here inflated the Assignments total and folded an
  // unconfirmed request's price into Outstanding as if it were owed money.
  const activeAssignments = useMemo(
    () => assignments.filter(a => !a.archived && a.status !== 'pending_confirmation'),
    [assignments],
  );
  // Same resolution as the cost figure above — a bundle assignment consumes the
  // fabrics inside the bundle, which is why these bars all read zero before.
  const assignedAttribution = useMemo(
    () => attributeFabricUnits(activeAssignments, fabrics, bundles),
    [activeAssignments, fabrics, bundles],
  );
  const bundleSellability = useMemo(
    () => computeBundleSellability(
      // Favour bundles (Groomsmen Box, Traditional Wedding Box, etc.) draw
      // from the separate favours/favour_assignments stock domain, not
      // fabrics — including them here just showed a permanent, meaningless
      // "no resolvable fabric items" row for every one of them.
      bundles.filter(b => b.is_active && b.types.includes('asoebi')),
      fabrics,
      activeAssignments,
      assignedAttribution,
    ),
    [bundles, fabrics, activeAssignments, assignedAttribution],
  );
  const fabricChartData = useMemo(() =>
    fabrics
      .filter(f => f.is_active && !f.archived)
      .map(f => {
        const assignedYards = assignedAttribution.unitsByFabric[String(f.id)] ?? 0;
        return {
          name: f.name.length > 14 ? `${f.name.slice(0, 13)}…` : f.name,
          fullName: f.name,
          Stock: f.stock || 0,
          Assigned: assignedYards,
        };
      })
      .sort((a, b) => b.Stock - a.Stock)
      .slice(0, 8),
  [fabrics, assignedAttribution]);

  const paymentStatusData = useMemo(() => {
    const counts: Record<string, number> = { paid: 0, partial: 0, unpaid: 0 };
    activeAssignments.forEach(a => {
      const status = a.payment_status ?? 'unpaid';
      counts[status] = (counts[status] ?? 0) + 1;
    });
    return [
      { name: 'Paid', value: counts.paid, color: '#16a34a' },
      { name: 'Partial', value: counts.partial, color: '#d97706' },
      { name: 'Unpaid', value: counts.unpaid, color: '#dc2626' },
    ].filter(d => d.value > 0);
  }, [activeAssignments]);

  // Payment Status used to be its own donut card, showing the same fact the
  // Guest Cost Coverage risk line already states in ₦ terms — folded in here
  // as a % instead of duplicating a whole chart for one number.
  const paidAssignmentPct = useMemo(() => {
    const total = paymentStatusData.reduce((sum, d) => sum + d.value, 0);
    if (total === 0) return null;
    const paid = paymentStatusData.find(d => d.name === 'Paid')?.value ?? 0;
    return (paid / total) * 100;
  }, [paymentStatusData]);

  // Waterfall bars for Guest Cost Coverage: Price falling to Net through two
  // cost steps. Each bar stacks an invisible `base` under the visible
  // `amount`, so the visible segment floats at the right height instead of
  // starting from zero — the standard recharts technique for waterfalls.
  const buildWaterfallData = (coverage: GuestCostCoverage) => {
    const afterFabric = coverage.avgPackagePrice - coverage.avgPackageCost;
    const net = coverage.netMargin;
    return [
      { name: 'Price', base: 0, amount: coverage.avgPackagePrice, kind: 'start' as const },
      { name: 'Fabric', base: afterFabric, amount: coverage.avgPackagePrice - afterFabric, kind: 'cost' as const },
      { name: 'Hosting', base: net, amount: afterFabric - net, kind: 'cost' as const },
      { name: 'Net', base: Math.min(0, net), amount: Math.abs(net), kind: net >= 0 ? ('positive' as const) : ('negative' as const) },
    ];
  };
  const WATERFALL_COLORS: Record<string, string> = {
    start: '#8b5a3c',
    cost: '#dc2626',
    positive: '#16a34a',
    negative: '#dc2626',
  };
  const WaterfallTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ dataKey?: string; payload?: { name: string; amount: number; kind: string } }> }) => {
    const point = active ? payload?.find(p => p.dataKey === 'amount')?.payload : undefined;
    if (!point) return null;
    return (
      <div className="asoebi-waterfall-tooltip">
        <strong>{point.name}</strong>: {point.kind === 'cost' ? '−' : ''}{fmtN(point.amount)}
      </div>
    );
  };

  // ── Bundle Economics radar ──
  // Only bundles with real landed-cost data go on the radar — a bundle never
  // sourced yet would otherwise show a fabricated 0 for its most interesting
  // axis. Capped at 6 series (by assigned count, then price) so the chart
  // stays legible; there's no fixed bundle-count ceiling in the data model.
  const RADAR_AXES: { key: 'price' | 'targetCost' | 'landedCost' | 'margin' | 'assignedCount'; label: string }[] = [
    { key: 'price', label: 'Price' },
    { key: 'targetCost', label: 'Target Cost' },
    { key: 'landedCost', label: 'Landed Cost' },
    { key: 'margin', label: 'Margin' },
    { key: 'assignedCount', label: 'Assigned' },
  ];
  const RADAR_COLORS = ['#8b5a3c', '#d4a574', '#2563eb', '#16a34a', '#d97706', '#7c3aed'];
  // recharts v3 types PolarAngleAxis's return as ReactNode (which includes
  // `undefined`), which @types/react 18's stricter JSX component typing
  // rejects — a known recharts v3 typing gap, not a real runtime issue.
  // Cast through a loose component type rather than pulling in another
  // charting library or type package for one component.
  const PolarAngleAxisTyped = PolarAngleAxis as unknown as React.ComponentType<Record<string, unknown>>;
  const radarChart = useMemo(() => {
    const eligible = bundleEconomics.filter(b => b.landedCost !== null);
    if (eligible.length === 0) return null;
    const bundlesUsed = [...eligible]
      .sort((a, b) => b.assignedCount - a.assignedCount || b.price - a.price)
      .slice(0, 6);
    const rawValue = (b: typeof bundlesUsed[number], key: (typeof RADAR_AXES)[number]['key']): number | null =>
      key === 'assignedCount' ? b.assignedCount : b[key];
    const data = RADAR_AXES.map(axis => {
      const values = bundlesUsed.map(b => rawValue(b, axis.key) ?? 0);
      const min = Math.min(0, ...values);
      const max = Math.max(0, ...values);
      const span = max - min || 1;
      const row: Record<string, string | number> = { axis: axis.label };
      bundlesUsed.forEach(b => {
        const raw = rawValue(b, axis.key);
        row[b.bundleName] = raw === null ? 0 : Math.round(((raw - min) / span) * 100);
        row[`${b.bundleName}__raw`] = raw === null ? NaN : raw;
      });
      return row;
    });
    return { data, names: bundlesUsed.map(b => b.bundleName) };
  }, [bundleEconomics]);

  const RadarTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ dataKey?: string; payload?: Record<string, unknown> }>; label?: string }) => {
    if (!active || !payload?.length) return null;
    const isCount = label === 'Assigned';
    return (
      <div className="asoebi-radar-tooltip">
        <strong>{label}</strong>
        {payload.map(p => {
          const key = String(p.dataKey ?? '');
          const raw = p.payload?.[`${key}__raw`];
          const rawNum = typeof raw === 'number' ? raw : NaN;
          return (
            <div key={key}>
              {key}: {Number.isNaN(rawNum) ? 'no data' : (isCount ? rawNum.toLocaleString() : fmtN(rawNum))}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Spend by Budget Category donut ──
  const DONUT_COLORS = ['#8b5a3c', '#d4a574', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626', '#0891b2', '#be185d', '#65a30d', '#78716c'];
  const categorySpendChartData = useMemo(
    () => categorySpend
      .filter(c => c.total_spent_ngn > 0)
      .map((c, i) => ({ name: c.category_name, value: c.total_spent_ngn, color: DONUT_COLORS[i % DONUT_COLORS.length] })),
    [categorySpend],
  );

  // ── Fabric Utilization by Bundle heatmap ──
  // Per-bundle attribution (attributeFabricUnits scoped to that bundle's own
  // assignments) rather than the page-wide assignedAttribution above, which
  // sums every bundle's consumption of a shared fabric into one number —
  // exactly the per-bundle breakdown the sellability table's single
  // "Constrained by" column can't show.
  const fabricBundleMatrix = useMemo(() => {
    const asoebiBundles = bundles.filter(b => b.is_active && b.types.includes('asoebi'));
    if (asoebiBundles.length === 0) return null;
    const perBundle = asoebiBundles.map(bundle => ({
      bundle,
      attribution: attributeFabricUnits(
        activeAssignments.filter(a => refersToBundle(a, bundle.id, bundles)),
        fabrics,
        bundles,
      ),
      bottleneckFabric: bundleSellability.find(s => s.bundleId === bundle.id)?.bottleneckFabric ?? null,
    }));
    const rows = fabrics
      .filter(f => f.is_active && !f.archived)
      .map(f => {
        const cells = perBundle.map(({ bundle, attribution, bottleneckFabric }) => {
          const units = attribution.unitsByFabric[String(f.id)] ?? 0;
          const pct = (f.stock || 0) > 0 ? (units / (f.stock || 1)) * 100 : (units > 0 ? 100 : 0);
          return { bundleId: bundle.id, bundleName: bundle.name, units, pct, isBottleneck: units > 0 && bottleneckFabric === f.name };
        });
        return { fabricId: f.id, fabricName: f.name, stock: f.stock || 0, cells };
      })
      .filter(row => row.cells.some(c => c.units > 0));
    return { bundles: asoebiBundles, rows };
  }, [bundles, fabrics, activeAssignments, bundleSellability]);

  const heatBucketClass = (pct: number): string => {
    if (pct <= 0) return 'heat-0';
    if (pct < 25) return 'heat-25';
    if (pct < 50) return 'heat-50';
    if (pct < 75) return 'heat-75';
    if (pct < 100) return 'heat-99';
    return 'heat-100';
  };

  const mostAssignedFabric = [...fabricChartData].sort((a, b) => b.Assigned - a.Assigned)[0];
  // Distinct from "unpaid": a guest with SOME money on file (payment_status
  // 'partial'), not one who hasn't paid anything yet — the card below reads
  // "Partially Paid" and previously counted both, so a ₦0/never-paid guest
  // (e.g. Kassidy Agba, order 43) inflated a card meant to flag part-payments.
  const partiallyPaidGuestCount = new Set(
    activeAssignments.filter(a => a.payment_status === 'partial').map(a => a.guest_id)
  ).size;
  const avgOrderValue = activeAssignments.length > 0
    ? Math.round(Number(sumDecimal(activeAssignments.map(a => a.total_price))) / activeAssignments.length)
    : 0;

  // Collected/Outstanding cards moved to the Orders tab (PortalOrdersTriage)
  // now that Orders lists the full order population — trackerOutstanding
  // itself stays, still used by the Overview tab's revenue-at-risk summary.
  // The Assignments-tab Assignments/Draft/Complete cards that also lived here
  // are gone with the tab's old contents: they counted ORDERS by delivery
  // status, which is not what that tab lists any more. AsoebiAssignmentsView
  // renders its own three cards over real asoebi_assignments rows instead.
  const trackerOutstanding = Number(sumDecimal(
    activeAssignments
      .filter(a => (a.payment_status ?? 'unpaid') !== 'paid')
      .map(a => subtractDecimal(a.total_price, a.payment_amount ?? '0') ?? '0')
  ));

  // ── Guest Cost Coverage ──
  const completedCount = assignments.filter(a => COMPLETED_STATUSES.includes(a.status)).length;
  const asoebiEventIds = useMemo(
    () => [...new Set(bundles.flatMap(b => b.eventIds ?? []))],
    [bundles],
  );
  const hostingCost = useMemo(
    () => computeHostingCostBreakdown(budgetCategories, events, asoebiEventIds, guestStats),
    [budgetCategories, events, asoebiEventIds, guestStats],
  );
  const avgPackagePrice = completedCount > 0 ? revenue / completedCount : 0;
  const avgPackageCost = completedCount > 0 ? totalCost / completedCount : 0;
  const coveragePlanned = computeGuestCostCoverage(
    avgPackagePrice, avgPackageCost,
    hostingCost.plannedGuestBasis > 0 ? hostingCost.total / hostingCost.plannedGuestBasis : 0,
  );
  const coverageActual = computeGuestCostCoverage(
    avgPackagePrice, avgPackageCost,
    hostingCost.actualGuestBasis > 0 ? hostingCost.total / hostingCost.actualGuestBasis : 0,
  );

  // Snapshot CSV of the numbers on screen, not a raw table dump — one row
  // per figure so it reads the same in a spreadsheet as it does on the
  // dashboard, rather than forcing the reader to re-derive Coverage % or
  // Net Margin themselves from a row-per-assignment export.
  const handleExportAccountingReport = () => {
    const summaryRows: string[][] = [
      ['Metric', 'Value'],
      ['Total Asoebi Revenue (sold)', String(revenue)],
      ['Total Fabric Cost (sold)', String(totalCost)],
      ['Profit on Sold (fabric only)', String(profit)],
      ['Total Spent (procurement)', totalSpent !== null ? String(totalSpent) : ''],
      ['', ''],
      ['Avg Package Price', String(Math.round(avgPackagePrice))],
      ['Avg Fabric Cost', String(Math.round(avgPackageCost))],
      ['Food & Drinks (Catering + Drinks & Beverages, actual spend)', String(Math.round(hostingCost.foodAndDrinks))],
      ['Seating & Arrangements (Venue, actual spend)', String(Math.round(hostingCost.seatingAndArrangements))],
      ['', ''],
      ['Max invite-list basis (events.headcount — invite ceiling, not venue capacity)', String(hostingCost.plannedGuestBasis)],
      ['Hosting Cost per Guest (At Max Invited)', String(Math.round(coveragePlanned.hostingCostPerGuest))],
      ['Net Guest Margin (At Max Invited)', String(Math.round(coveragePlanned.netMargin))],
      ['Coverage % (At Max Invited)', `${coveragePlanned.coveragePct.toFixed(1)}%`],
      ['', ''],
      ['Actual guest basis (confirmed + plus-ones)', String(hostingCost.actualGuestBasis)],
      ['Hosting Cost per Guest (Actual)', String(Math.round(coverageActual.hostingCostPerGuest))],
      ['Net Guest Margin (Actual)', String(Math.round(coverageActual.netMargin))],
      ['Coverage % (Actual)', `${coverageActual.coveragePct.toFixed(1)}%`],
      ['', ''],
      ['Revenue at Risk (unpaid/partial)', String(Math.round(trackerOutstanding))],
      [''],
      ['Bundle Stock Availability', ''],
      ['Bundle', 'Makeable from stock', 'Sellable now', 'Already reserved', 'Paid', 'Constrained by'],
      ...bundleSellability.map(b => [
        b.bundleName,
        String(b.makeableFromStock),
        String(b.sellableNow),
        String(b.reservedSets),
        String(b.paidSets),
        b.bottleneckFabric ?? '',
      ]),
    ];
    const csv = summaryRows
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadText('asoebi-guest-cost-coverage-report.csv', csv);
  };

  useEffect(() => {
    if (triggerReport) {
      handleExportAccountingReport();
      onReportHandled?.();
    }
  }, [triggerReport]);

  // Fabric detail page: render only FabricCatalog with fabricId
  if (fabricId) {
    if (loading) {
      return (
        <div className="wt-container fade-in">
          <EmptyState icon="list" title="Loading fabric…" compact />
        </div>
      );
    }
    return (
      <div className="wt-container fade-in">
        <FabricCatalog
          fabrics={fabrics}
          onUpdate={handleFabricUpdate}
          onDelete={handleFabricDelete}
          filter={filter}
          onFilterChange={setFilter}
          fabricId={fabricId}
        />
      </div>
    );
  }

  // Payment inline form page — replaces main content
  if (showPaymentModal && selectedAssignment) {
    return (
      <div className="wt-container fade-in">
        <PaymentModal
          assignment={selectedAssignment}
          // A bundle assignment has no fabric row at all, so this lookup is
          // legitimately undefined for most live rows — itemLabel carries the
          // name in either case (the non-null assertion here used to hand
          // PaymentModal `undefined` and crash it on `fabric.name`).
          fabric={fabrics.find(f => f.id === selectedAssignment.fabric_id)}
          itemLabel={fabricRefName(selectedAssignment, fabrics, bundles, 'Asoebi package')}
          onPaymentComplete={handlePaymentComplete}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedAssignment(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="wt-container fade-in">

      {activeTab === 'overview' && (
        <section className="stats-grid asoebi-overview-cards">
          <MetricCard label="Bundles" value={bundleCount} tone="purple" icon="grid" onClick={() => setActiveTab('bundles')} />
          <MetricCard label="Fabric Stock" value={`${totalFabricStock.toLocaleString()} yds`} tone="pink" icon="list" onClick={() => setActiveTab('fabrics')} />
          <MetricCard label="Partially Paid" value={partiallyPaidGuestCount} tone="orange" icon="users" onClick={() => { setAsoebiOrdersBar({ status: '', payment: 'partial' }); setActiveTab('orders'); }} />
          {/* "Total Spent" (real procurement cash out) and "Margin on Sold"/"Profit
              on Sold" (guest-sale economics) are deliberately two different
              numbers, not duplicates — conflating them under one "Cost"/"Profit"
              label made ~₦9.6M in real spend invisible here. See the totalSpent
              fetch above and reference_ans_fx_rate_column_unreliable-adjacent
              scrutiny that caught it. */}
          <MetricCard
            label="Total Spent"
            value={totalSpent !== null ? fmtN(totalSpent) : '—'}
            tone="red"
            icon="chart"
            onClick={() => setActiveTab('orders')}
          />
          <MetricCard label="Fabric Cost (Sold)" value={fmtN(totalCost)} tone="orange" icon="chart" onClick={() => setActiveTab('orders')} />
          <MetricCard label="Profit (Sold)" value={fmtN(profit)} tone={profit >= 0 ? 'green' : 'red'} icon="chart" onClick={() => setActiveTab('orders')} />
        </section>
      )}

      {/* Payment Status (donut) and Bundle Stock Availability (bar chart) used
          to sit here too, each duplicating a number shown better elsewhere:
          Payment Status restated what the Guest Cost Coverage risk line
          already said, and the Bundle Stock Availability chart restated the
          exact same makeable/reserved/sellable figures as the detail table
          right below it — a chart and a table both saying "63 sellable" for
          the same bundle. Removed rather than kept "for the same info,
          differently shaped" — Payment Status folded into Guest Cost
          Coverage as a %, Bundle Stock Availability's numbers stayed in the
          table, which also carries the bottleneck-fabric column no chart did. */}
      {activeTab === 'overview' && (fabricChartData.length > 0 || completedCount > 0) && (
        <div className="asoebi-overview-row">
          {fabricChartData.length > 0 && (
            <section className="side-section">
              <h4>Stock vs Assigned by Fabric</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={fabricChartData} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''} formatter={(v: unknown) => `${v} yds`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Stock" fill="#d4a574" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="Assigned" fill="#8b5a3c" radius={[3, 3, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </section>
          )}

          {completedCount > 0 && (
            <section className="side-section asoebi-coverage-section">
              <h4>Guest Cost Coverage</h4>
              <p className="asoebi-coverage-sub">
                What the average asoebi package earns against fabric cost plus this guest's share of catering, drinks, and their seat — not just fabric margin.
              </p>
              <div className="asoebi-coverage-rows">
                {[
                  { label: 'At Max Invited', basis: hostingCost.plannedGuestBasis, coverage: coveragePlanned },
                  { label: 'Actual (confirmed so far)', basis: hostingCost.actualGuestBasis, coverage: coverageActual },
                ].map(row => (
                  <div key={row.label} className="asoebi-coverage-row">
                    <div className="asoebi-coverage-row-header">
                      <span>{row.label} · {row.basis.toLocaleString()} guests</span>
                      <strong className={row.coverage.netMargin >= 0 ? 'asoebi-coverage-positive' : 'asoebi-coverage-negative'}>
                        {fmtN(row.coverage.netMargin)} net {row.coverage.netMargin >= 0 ? 'margin' : 'shortfall'} / guest
                      </strong>
                    </div>
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={buildWaterfallData(row.coverage)} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis hide domain={['dataMin - 5000', 'dataMax + 5000']} />
                        <Tooltip content={<WaterfallTooltip />} cursor={{ fill: 'transparent' }} />
                        <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
                        <Bar dataKey="amount" stackId="wf" radius={[3, 3, 3, 3]} maxBarSize={40} isAnimationActive={false}>
                          {buildWaterfallData(row.coverage).map((d, i) => (
                            <Cell key={i} fill={WATERFALL_COLORS[d.kind]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="asoebi-coverage-breakdown">
                      <span>Price {fmtN(row.coverage.avgPackagePrice)}</span>
                      <span>− Fabric {fmtN(row.coverage.avgPackageCost)}</span>
                      <span>− Hosting {fmtN(row.coverage.hostingCostPerGuest)}</span>
                      <span>= {row.coverage.coveragePct.toFixed(0)}% covered</span>
                    </div>
                  </div>
                ))}
              </div>
              {(paidAssignmentPct !== null || trackerOutstanding > 0) && (
                <p className="asoebi-coverage-sub asoebi-coverage-risk">
                  {paidAssignmentPct !== null && `${paidAssignmentPct.toFixed(0)}% of assignments paid in full`}
                  {trackerOutstanding > 0 && `${paidAssignmentPct !== null ? ' · ' : ''}${fmtN(trackerOutstanding)} still unpaid across partially-paid/unpaid guests — the coverage above assumes it collects.`}
                </p>
              )}
            </section>
          )}
        </div>
      )}

      {/* Bundle Economics radar + Spend by Budget Category donut — additive to
          everything above: the radar is the first place Price/Target
          Cost/Landed Cost/Margin/Assigned sit on ONE bundle-by-bundle chart
          (they previously only existed scattered across BundlesView's edit
          form, one bundle at a time), and the donut is the first view of
          real procurement cash across every category, not just Asoebi's
          "Total Spent" card above. */}
      {activeTab === 'overview' && (radarChart || categorySpendChartData.length > 0) && (
        <div className="asoebi-overview-row">
          <section className="side-section asoebi-radar-section">
            <h4>Bundle Economics</h4>
            <p className="asoebi-coverage-sub">
              Price, target cost, real landed cost, margin, and units assigned — one line per bundle, each axis scaled to the bundles shown so shapes are comparable at a glance.
            </p>
            {radarChart ? (
              <ResponsiveContainer width="100%" height={320}>
                <RadarChart data={radarChart.data} margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
                  <PolarGrid />
                  <PolarAngleAxisTyped dataKey="axis" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                  <Tooltip content={<RadarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {radarChart.names.map((name, i) => (
                    <Radar key={name} name={name} dataKey={name} stroke={RADAR_COLORS[i % RADAR_COLORS.length]} fill={RADAR_COLORS[i % RADAR_COLORS.length]} fillOpacity={0.15} />
                  ))}
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState icon="chart" title="No bundles with landed cost data yet" compact />
            )}
          </section>

          <section className="side-section asoebi-category-spend-section">
            <h4>Spend by Budget Category</h4>
            <p className="asoebi-coverage-sub">
              Real procurement cash paid (or committed) across every budget category — not just Asoebi's own total above.
            </p>
            {categorySpendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={categorySpendChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                    {categorySpendChartData.map(d => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: unknown) => fmtN(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} layout="vertical" verticalAlign="middle" align="right" />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState icon="chart" title="No procurement spend recorded yet" compact />
            )}
          </section>
        </div>
      )}

      {activeTab === 'overview' && (
        <section className="side-section asoebi-heatmap-section">
          <h4>Fabric Utilization by Bundle</h4>
          <p className="asoebi-coverage-sub">
            How much of each fabric's stock is tied up in each bundle — darker cells sit closer to exhausting that fabric; ◆ marks that bundle's own tightest constraint from the table below.
          </p>
          {fabricBundleMatrix && fabricBundleMatrix.rows.length > 0 ? (
            <div className="asoebi-heatmap-wrap">
              <table className="asoebi-heatmap-table">
                <thead>
                  <tr>
                    <th>Fabric</th>
                    {fabricBundleMatrix.bundles.map(b => <th key={b.id}>{b.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {fabricBundleMatrix.rows.map(row => (
                    <tr key={row.fabricId}>
                      <td className="asoebi-heatmap-rowlabel">{row.fabricName}</td>
                      {row.cells.map(cell => (
                        <td
                          key={cell.bundleId}
                          className={`asoebi-heatmap-cell ${heatBucketClass(cell.pct)}`}
                          title={`${cell.units.toLocaleString()} of ${row.stock.toLocaleString()} yds (${cell.pct.toFixed(0)}%)`}
                        >
                          {cell.units > 0 ? `${cell.pct.toFixed(0)}%${cell.isBottleneck ? ' ◆' : ''}` : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon="grid" title="No fabric committed to a bundle yet" compact />
          )}
        </section>
      )}

      {activeTab === 'overview' && bundleSellability.length > 0 && (
        <section className="side-section asoebi-sellability-section">
          <h4>Bundle Stock Availability</h4>
          <p className="asoebi-coverage-sub">
            "Sellable now" is net of every fabric already promised to another
            guest — the safe number to quote without overselling.
          </p>
          <div className="asoebi-sellability-table-wrap">
            <table className="asoebi-sellability-table">
              <thead>
                <tr>
                  <th>Bundle</th>
                  <th>Makeable from stock</th>
                  <th>Sellable now</th>
                  <th>Already reserved</th>
                  <th>Paid</th>
                  <th>Constrained by</th>
                </tr>
              </thead>
              <tbody>
                {bundleSellability.map(b => (
                  <tr key={b.bundleId}>
                    <td>{b.bundleName}</td>
                    <td>{b.makeableFromStock}</td>
                    <td className={b.sellableNow <= 0 ? 'asoebi-sellability-zero' : undefined}>
                      {b.sellableNow}
                    </td>
                    <td>{b.reservedSets}</td>
                    <td>{b.paidSets}</td>
                    <td className="asoebi-sellability-muted">{b.bottleneckFabric ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === 'fabrics' && (
        <FabricCatalog
          fabrics={filteredFabrics}
          onUpdate={handleFabricUpdate}
          onDelete={handleFabricDelete}
          filter={filter}
          onFilterChange={setFilter}
        />
      )}

      {/* Real asoebi_assignments rows (AZ-xxx) — fulfillment records, each
          pointing at ONE order. This tab used to render GuestAsoebiTracker,
          which lists ORDERS (AOS-xxx): the same row was served at both URLs
          with the same number, so AZ-58 and AOS-58 looked like one record.
          They are separate tables with separate id spaces. GuestAsoebiTracker
          keeps the order detail page (/asoebi/orders/:id) unchanged. */}
      {/* Packing 'Assignments' tab removed: the asoebi/fulfillment packing API has not been ported to port/kal yet (see the build plan). */}

      {activeTab === 'orders' && showOrderDetail && (
        <GuestAsoebiTracker
          assignments={assignments}
          guestProfiles={guestProfiles}
          fabrics={fabrics}
          onPayment={handlePayment}
          onUpdate={loadData}
          triggerAssignForm={triggerAssignForm}
          onAssignFormOpened={() => setTriggerAssignForm(false)}
          statusFilter={trackerFilter}
        />
      )}

      {activeTab === 'orders' && !showOrderDetail && <AsoebiOrdersList fabrics={fabrics} bundles={bundles} />}

      {activeTab === 'bundles' && <BundlesView openBundleId={openBundleId} openNewBundleOnMount={openNewBundleOnMount} />}

      {/* Deliveries dashboard deferred to a follow-up PR: the backend (asoebi/deliveries, zone-fees,
          fee-summary) is live and tested, but this screen needs order-line-level delivery linking the
          order read model doesn't expose yet — see the build plan's 'missing' list. */}

      {/* Recent Assignments — deliveries/portal tabs have their own stats/list,
          so skip it there; the bundles tab's inline form also skips it, since
          it shows its own "Recent Assignments" sidebar card instead
          (BundlesView.tsx) */}
      {activeTab !== 'orders' && !(activeTab === 'bundles' && bundleFormOpen) && (
        <ActivityLog
          activities={recentAssignments.map(assignment => {
            // fabric_id can point at a bundle or at a fabric; fabric_kind says
            // which, so the unit reads "packages" vs "yards" correctly even
            // when the two id spaces collide.
            const isBundle = isBundleRef(assignment, bundles);
            const name = fabricRefName(assignment, fabrics, bundles, 'Unknown Fabric');
            const unit = isBundle ? (Number(assignment.yards) === 1 ? 'package' : 'packages') : (Number(assignment.yards) === 1 ? 'yard' : 'yards');
            return {
              id: assignment.id,
              action: `${name} assigned (${assignment.yards} ${unit} - ${formatMoney(Number(assignment.total_price), assignment.currency)})`,
              timestamp: formatDate(assignment.created_at),
              user: user?.name ?? 'Asoebi Manager'
            };
          })}
          title="Recent Assignments"
          className="asoebi-activity-log"
        />
      )}

      <ConfirmDialog
        open={deleteFabricTarget !== null}
        onClose={() => setDeleteFabricTarget(null)}
        onConfirm={async () => {
          const fabricId = deleteFabricTarget!;
          setDeleteFabricTarget(null);
          try {
            await deleteFabric(fabricId);
            setFabrics(fabrics.filter(f => f.id !== fabricId));
            setStats(getAsoebiStats());
            showSuccess('Fabric deleted.');
          } catch (err) {
            // deleteFabric now throws both for a refused delete (not archived)
            // and for a rejected DELETE, so neither can pass as a success.
            showError(err instanceof Error ? err.message : 'Failed to delete fabric.');
          }
        }}
        title="Delete fabric?"
        description="Are you sure you want to delete this fabric? This action cannot be undone."
      />
    </div>
  );
};

export default AsoebiManager;
